"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Globe,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { VenueFAQFormDialog } from "./venue-faq-form-dialog";
import { useAuth } from "@/contexts/auth-and-permissions-context";
import { ROLES } from "@/lib/constants/roles";

interface FAQ {
  faq_id: number;
  venue_id: number;
  questions: Record<string, string>;
  answers: Record<string, string>;
  display_order: number;
  status: { id: number; name: string };
  created_at: string;
  updated_at: string;
}

interface VenueFAQsClientProps {
  venueId: string;
}

export function VenueFAQsClient({ venueId }: VenueFAQsClientProps) {
  const { user, isLoading: authLoading } = useAuth();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);
  const [venueLanguages, setVenueLanguages] = useState<string[]>([]);
  const [displayLanguage, setDisplayLanguage] = useState<string>("");

  // Check if user has edit permissions (superadmin or booking staff)
  const canEdit =
    user?.role_id === ROLES.SUPER_ADMIN ||
    user?.role_id === ROLES.BOOKING_STAFF;

  const fetchVenueLanguages = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/details`);
      const data = await response.json();

      if (data.success && data.data?.languages) {
        const languages = data.data.languages;
        setVenueLanguages(languages);
        setDisplayLanguage(languages.includes("en") ? "en" : languages[0]);
      } else {
        // Default to English if no languages found
        setVenueLanguages(["en"]);
        setDisplayLanguage("en");
      }
    } catch (error) {
      console.error("Error fetching venue languages:", error);
      setVenueLanguages(["en"]);
      setDisplayLanguage("en");
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/faqs`);
      const data = await response.json();

      if (data.success) {
        setFaqs(data.data || []);
      } else {
        toast.error("Failed to load FAQs");
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenueLanguages();
    fetchFAQs();
  }, [venueId]);

  const filteredFAQs = Array.isArray(faqs)
    ? faqs.filter((faq) => {
        try {
          // Safety checks
          if (!faq || !faq.status || !faq.questions || !faq.answers) {
            return false;
          }

          // Only show active FAQs for non-superadmin users
          if (!canEdit && faq.status.name !== "active") {
            return false;
          }

          // Only show FAQs that have content in the selected language
          if (!displayLanguage) {
            return false;
          }

          const hasContentInLanguage =
            faq.questions[displayLanguage] && faq.answers[displayLanguage];
          if (!hasContentInLanguage) {
            return false;
          }

          const question = faq.questions[displayLanguage];
          const answer = faq.answers[displayLanguage];
          return (
            question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            answer.toLowerCase().includes(searchTerm.toLowerCase())
          );
        } catch (error) {
          return false;
        }
      })
    : [];

  const handleAdd = () => {
    setEditingFaq(null);
    setIsFormDialogOpen(true);
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setIsFormDialogOpen(true);
  };

  const handleDeleteClick = (faq: FAQ) => {
    setFaqToDelete(faq);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;

    try {
      const response = await fetch(
        `/api/venues/${venueId}/faqs/${faqToDelete.faq_id}`,
        {
          method: "DELETE",
        },
      );

      const data = await response.json();

      if (data.success) {
        toast.success("FAQ deleted successfully");
        setDeleteConfirmOpen(false);
        setFaqToDelete(null);
        fetchFAQs();
      } else {
        toast.error(data.error || "Failed to delete FAQ");
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ");
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setFaqToDelete(null);
  };

  const handleSave = async (faqData: {
    questions: Record<string, string>;
    answers: Record<string, string>;
    status_id?: number;
    display_order?: number;
  }) => {
    try {
      const url = editingFaq
        ? `/api/venues/${venueId}/faqs/${editingFaq.faq_id}`
        : `/api/venues/${venueId}/faqs`;

      const method = editingFaq ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...faqData,
          display_order: faqData.display_order ?? faqs.length,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(
          editingFaq ? "FAQ updated successfully" : "FAQ created successfully",
        );
        setIsFormDialogOpen(false);
        fetchFAQs();
      } else {
        toast.error(data.error || "Failed to save FAQ");
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Failed to save FAQ");
    }
  };

  const getDisplayText = (faq: FAQ, field: "questions" | "answers") => {
    const text = faq[field][displayLanguage] || "";
    return text;
  };

  // Add error boundary-like behavior
  if (!venueId) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-red-600">Error: No venue ID provided</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">FAQ management</h1>
            <p className="text-muted-foreground text-sm">
              Manage frequently asked questions for your venue
            </p>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add FAQ
          </Button>
        </div>

        <div className="space-y-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Display language:</span>
            <div className="flex gap-1">
              {venueLanguages.map((lang) => (
                <Button
                  key={lang}
                  variant={displayLanguage === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDisplayLanguage(lang)}
                  className={
                    displayLanguage === lang
                      ? "bg-purple-600 hover:bg-purple-700"
                      : ""
                  }
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? `No FAQs found matching your search in ${displayLanguage.toUpperCase()}`
                : faqs.length > 0
                  ? `No FAQs available in ${displayLanguage.toUpperCase()}`
                  : "No FAQs created yet"}
            </p>
            {!searchTerm && (
              <Button onClick={handleAdd}>
                <Plus className="w-4 h-4 mr-2" />
                Create{" "}
                {faqs.length > 0
                  ? `FAQ in ${displayLanguage.toUpperCase()}`
                  : "your first FAQ"}
              </Button>
            )}
          </div>
        ) : (
          <div className="mt-6">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-100">
                  <TableHead className="font-medium text-gray-900 text-sm h-12">
                    Question
                  </TableHead>
                  <TableHead className="font-medium text-gray-900 text-sm">
                    Status
                  </TableHead>
                  <TableHead className="font-medium text-gray-900 text-sm text-center">
                    Order
                  </TableHead>
                  <TableHead className="font-medium text-gray-900 text-sm">
                    Updated
                  </TableHead>
                  <TableHead className="font-medium text-gray-900 text-sm text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFAQs.map((faq) => (
                  <TableRow
                    key={faq.faq_id}
                    className="border-b border-gray-50 hover:bg-gray-50/50"
                  >
                    <TableCell className="py-4">
                      <p className="text-gray-900 font-normal">
                        {getDisplayText(faq, "questions")}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-800">
                        {faq.status.name === "active" ? (
                          <Eye className="w-3.5 h-3.5" />
                        ) : (
                          <EyeOff className="w-3.5 h-3.5" />
                        )}
                        <span className="text-xs font-medium">
                          {faq.status.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-gray-900">
                      {faq.display_order}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(faq.updated_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(faq)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(faq)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <VenueFAQFormDialog
          open={isFormDialogOpen}
          onOpenChange={setIsFormDialogOpen}
          faq={editingFaq}
          onSave={handleSave}
          venueLanguages={venueLanguages}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Delete FAQ?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this FAQ? This action cannot be
                undone.
              </DialogDescription>
            </DialogHeader>

            {faqToDelete && (
              <div className="my-4 p-4 bg-gray-50 rounded-lg border">
                <p className="font-medium text-gray-900 mb-2">
                  {faqToDelete.questions[displayLanguage] ||
                    "No content in selected language"}
                </p>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {faqToDelete.answers[displayLanguage] ||
                    "No content in selected language"}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete}>
                Delete FAQ
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  } catch (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h2 className="text-red-800 font-medium mb-2">
            Something went wrong
          </h2>
          <p className="text-red-600 text-sm mb-4">
            There was an error loading the FAQ page. Please try refreshing the
            page.
          </p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            Refresh page
          </Button>
        </div>
      </div>
    );
  }
}
